import {
    Alert,
    Button,
    Chip,
    Group,
    Loader,
    NumberInput,
    Paper,
    Select,
    Stack,
    Switch,
    Text,
    Textarea,
    Title,
} from '@mantine/core'
import {DatePickerInput} from '@mantine/dates'
import {useForm} from '@mantine/form'
import {notifications} from '@mantine/notifications'
import {useNavigate} from '@tanstack/react-router'
import {format} from 'date-fns'
import {useState} from 'react'

import {businessCalendarDate} from 'src/api/business-time'
import {parseDateInputValue} from 'src/jobs/job-schedule-format'
import {JobTimePicker} from 'src/jobs/job-time-picker'
import type {JobDetail} from 'src/jobs/schemas'
import {useJob} from 'src/jobs/use-jobs'
import {useActiveServiceTypes} from 'src/settings/use-service-types'

import {
    serviceVisitsFromJob,
    type ServiceVisitsValues,
    toServiceVisitsInput,
    validateServiceVisits,
    visitDays,
    visitsAddedTitle,
    WEEKDAY_NAMES,
} from './service-visits-logic'
import {useCreateServiceVisits} from './use-service-visits'

const DAYS_SHOWN = 12

type ServiceVisitsPageProps = {
    projectId: string
    orderLabel: string
    sourceJobId: string
    readOnly?: boolean
}

export function ServiceVisitsPage({
    projectId,
    orderLabel,
    sourceJobId,
    readOnly = false,
}: ServiceVisitsPageProps) {
    const {data: job, isLoading, isError} = useJob(sourceJobId)
    if (isLoading) {
        return <Loader aria-label="Loading the job" />
    }
    if (isError || !job) {
        return <Alert color="red">Could not load source job</Alert>
    }

    return (
        <ServiceVisitsForm
            key={job.id}
            projectId={projectId}
            orderLabel={orderLabel}
            job={job}
            readOnly={readOnly}
        />
    )
}

function ServiceVisitsForm({
    projectId,
    orderLabel,
    job,
    readOnly,
}: {
    projectId: string
    orderLabel: string
    job: JobDetail
    readOnly: boolean
}) {
    const navigate = useNavigate()
    const {data: categories = []} = useActiveServiceTypes()
    const createSchedule = useCreateServiceVisits(projectId)
    const form = useForm<ServiceVisitsValues>({
        initialValues: serviceVisitsFromJob(job, businessCalendarDate()),
    })
    const {values} = form
    const [triedSave, setTriedSave] = useState(false)
    const [saveError, setSaveError] = useState<string | null>(null)
    const allErrors = validateServiceVisits(values)
    const errors = triedSave ? allErrors : {}
    const days = visitDays(values)

    async function handleSave() {
        const input = toServiceVisitsInput(values, job)
        if (Object.keys(allErrors).length > 0 || !input) {
            setTriedSave(true)
            return
        }
        setSaveError(null)
        try {
            await createSchedule.mutateAsync(input)
            notifications.show({
                color: 'green',
                title: visitsAddedTitle(days.length, orderLabel),
                message: 'Added to the dispatch board',
            })
            await navigate({to: '/projects/$projectId', params: {projectId}})
        } catch (error) {
            setSaveError(error instanceof Error ? error.message : 'Failed to create service visits')
        }
    }

    return (
        <Stack gap="lg">
            <Alert color="blue" variant="light">
                Filled in from job {job.displayId} {job.name}, at{' '}
                {job.locations.map((location) => location.name).join(', ') || 'its site'}. No
                delivery or pickup is added.
            </Alert>
            <Paper withBorder p="md" radius="md">
                <Stack gap="md">
                    <Title order={3}>When</Title>
                    <Stack gap={4}>
                        <Text size="sm" fw={500}>
                            Every
                        </Text>
                        <Chip.Group
                            multiple
                            value={values.weekdays.map(String)}
                            onChange={(picked) =>
                                form.setFieldValue('weekdays', picked.map(Number))
                            }
                        >
                            <Group gap="xs">
                                {WEEKDAY_NAMES.map((name, weekday) => (
                                    <Chip key={name} value={String(weekday)}>
                                        {name}
                                    </Chip>
                                ))}
                            </Group>
                        </Chip.Group>
                        {errors['weekdays'] ? (
                            <Text size="xs" c="red">
                                {errors['weekdays']}
                            </Text>
                        ) : null}
                    </Stack>
                    <Group grow align="flex-start">
                        <DatePickerInput
                            label="First day"
                            withAsterisk
                            value={values.startDate}
                            error={errors['startDate']}
                            onChange={(value) =>
                                form.setFieldValue('startDate', parseDateInputValue(value))
                            }
                        />
                        <DatePickerInput
                            label="Last day"
                            withAsterisk
                            value={values.endDate}
                            error={errors['endDate']}
                            onChange={(value) =>
                                form.setFieldValue('endDate', parseDateInputValue(value))
                            }
                        />
                        <JobTimePicker
                            label="Visit time"
                            placeholder="Any time, e.g. 7am"
                            value={values.visitTime}
                            onChange={(visitTime) => form.setFieldValue('visitTime', visitTime)}
                        />
                    </Group>
                    <Text size="sm" c="dimmed" data-testid="visit-days">
                        {days.length === 0
                            ? 'No visits selected'
                            : `${days.length} ${days.length === 1 ? 'visit' : 'visits'}: ${days
                                  .slice(0, DAYS_SHOWN)
                                  .map((day) => format(day, 'EEE MMM d'))
                                  .join(', ')}${days.length > DAYS_SHOWN ? ' …' : ''}`}
                    </Text>
                </Stack>
            </Paper>
            <Paper withBorder p="md" radius="md">
                <Stack gap="md">
                    <Title order={3}>Visit details</Title>
                    <Select
                        label="Category"
                        withAsterisk
                        searchable
                        data={categories.map((category) => ({
                            value: category.id,
                            label: category.name,
                        }))}
                        {...form.getInputProps('serviceTypeId')}
                        error={errors['serviceTypeId']}
                    />
                    <Stack gap="xs">
                        <Text size="sm" fw={500}>
                            Crew and equipment per visit
                        </Text>
                        <Text size="xs" c="dimmed">
                            Equipment defaults to 0 per visit.
                        </Text>
                        {values.lines.map((line, index) => (
                            <Group key={`${line.kind}-${index}`} gap="sm">
                                <NumberInput
                                    aria-label={`${line.name} per visit`}
                                    min={0}
                                    allowDecimal={false}
                                    w={90}
                                    value={line.quantity}
                                    onChange={(quantity) =>
                                        form.setFieldValue(
                                            `lines.${index}.quantity`,
                                            Number(quantity) || 0,
                                        )
                                    }
                                />
                                <Text size="sm">{line.name}</Text>
                            </Group>
                        ))}
                        {errors['lines'] ? (
                            <Text size="xs" c="red">
                                {errors['lines']}
                            </Text>
                        ) : null}
                    </Stack>
                    <Textarea
                        label="Notes for each visit"
                        description="Copied to every visit"
                        autosize
                        minRows={3}
                        {...form.getInputProps('notes')}
                    />
                </Stack>
            </Paper>
            <Group justify="space-between">
                <Button
                    variant="subtle"
                    onClick={() => void navigate({to: '/jobs/$jobId', params: {jobId: job.id}})}
                >
                    Cancel
                </Button>
                <Group gap="md">
                    {saveError ? (
                        <Text size="sm" c="red" maw={360}>
                            {saveError}
                        </Text>
                    ) : null}
                    <Switch
                        label="Ready for dispatch"
                        {...form.getInputProps('readyForDispatch', {type: 'checkbox'})}
                    />
                    <Button
                        loading={createSchedule.isPending}
                        disabled={readOnly}
                        onClick={() => void handleSave()}
                    >
                        {days.length > 0 && Object.keys(allErrors).length === 0
                            ? `Set up ${days.length} ${days.length === 1 ? 'visit' : 'visits'}`
                            : 'Set up visits'}
                    </Button>
                </Group>
            </Group>
        </Stack>
    )
}
